import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";
import styles from "./Checkbox.module.scss";

export interface CheckboxProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
	indeterminate?: boolean;
	label?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	({ indeterminate = false, label, className = "", ...props }, ref) => {
		const innerRef = useRef<HTMLInputElement>(null);

		// Expose the input ref to parent components
		useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

		// Handle indeterminate state
		useEffect(() => {
			if (innerRef.current) {
				innerRef.current.indeterminate = indeterminate;
			}
		}, [indeterminate]);

		const checkboxStyles =
			"appearance-none w-16 h-16 border border-grayscale-300 rounded-4 outline-none cursor-pointer relative bg-white checked:bg-primary-500 checked:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed after:content-[''] after:absolute after:hidden checked:after:block after:left-[5px] after:top-[2px] after:w-[5px] after:h-[9px] after:border-white after:border-r-2 after:border-b-2 after:rotate-45";

		const combinedClassName = `${checkboxStyles} ${
			indeterminate ? styles.indeterminate : ""
		} ${className}`;

		if (label) {
			return (
				<label className="inline-flex items-center gap-8 cursor-pointer">
					<span className="relative flex items-center justify-center">
						<input
							ref={innerRef}
							type="checkbox"
							className={combinedClassName}
							style={{ margin: 0 }}
							{...props}
						/>
					</span>
					<span className="text-sm font-rubik text-grayscale-900 leading-tight">
						{label}
					</span>
				</label>
			);
		}

		return (
			<input
				ref={innerRef}
				type="checkbox"
				className={combinedClassName}
				style={{ margin: 0 }}
				{...props}
			/>
		);
	},
);

Checkbox.displayName = "Checkbox";
